/* eslint-env browser */
import PropTypes from 'prop-types';
import React from 'react';
import _ from 'lodash';
import axios from 'axios';
import { withRouter, Link, routerShape } from 'react-router';
import { connect } from 'react-redux';
import {
  Badge,
  Divider,
  Heading,
  Media,
  Message,
  Section,
  Space,
  Text,
} from 'rebass';
import { Flex, Box } from 'reflexbox';
import { Helmet } from 'react-helmet-async';
import * as Sentry from '@sentry/browser';

import {
  MAX_LEVEL,
  isTank,
  isHealer,
  avatar,
  races,
  getCharacterLinks,
  getCharacterSpecInfo,
  getIncorrectArmorPieces,
  hasDoubleOnUseEquipped,
} from 'shared/character';
import Measures from 'shared/measures';
import {
  uppercaseName,
  formatDps,
} from 'shared/string';
import {
  unionActors,
  getMaxMeasure,
  getEquippedGear,
  getAvgItemLevel,
  getTalentInfo,
  getSimErrorInfo,
} from 'shared/simcJsonTools';
import {
  fightStyles,
} from 'shared/simcOptions';
import { getReportTitleFromJson } from 'shared/pageMetadata';
import Droptimizer, { DIFFICULTIES } from 'shared/droptimizer';
import {
  CRAFTED_STAT_COMBOS,
  isHighSecondaryProcTrinket,
  getIncorrectMainStatPieces,
} from 'shared/items';
import {
  getGemInfo,
} from 'shared/gems';
import SInput from 'shared/simcinput';
import TalentTraits from 'shared/talentTraits';
import { findExpacOption } from 'shared/expansionOptions';

import GameData from 'shared/gameData';

import config from 'frontend/config';
import actions from 'frontend/state/actions';
import { selectors as userSelectors } from 'frontend/state/reducers/user';
import { selectors as siteSelectors } from 'frontend/state/reducers/site';
import { selectors as formSelectors } from 'frontend/state/reducers/simbot/form';
import { selectors as reportSelectors } from 'frontend/state/reducers/simbot/report';
import { DefaultError } from 'frontend/components/DefaultErrorBoundary';

import { retry, derror, DEVMODE } from 'frontend/util';
import {
  CopyToClipboard,
  DarkerBox,
  FallbackLoader,
  Icon,
  If,
  Loader,
  Responsive,
  WowIcon,
} from 'frontend/components';
import droptimizerLoad from 'frontend/droptimizerLoad';

import GearStrip from 'frontend/components/Simbot/GearStrip';
import TalentStrip from 'frontend/components/Simbot/TalentStrip';
//import TalentTree from 'frontend/components/Simbot/TalentTree';

import JobStatus from './JobStatus';
import StatWeightReport from './StatWeightReport';
import SimDetails from './SimDetails';
import ReportError from './ReportError';
import SimcInput from './SimcInput';
import SimcNotifications from './SimcNotifications';
import CopyTemplateButton from './CopyTemplateButton';
import TopGearPanel from './TopGearPanel';
import DroptimizerPanel from './DroptimizerPanel';
import ShareReport from './ShareReport';
import ActionSequence from './ActionSequence';
import DamageBreakdown from './DamageBreakdown';
import BuffReport from './BuffReport';

const ActorCompare = React.lazy(() => retry(() => import('./ActorCompare')));

function reportBaseUrl(simId) {
  if (process.env.NODE_ENV === 'production') {
    return `/reports/${simId}`;
  }
  return `https://www.raidbots.com/reports/${simId}`;
}

const TIMEOUT_GENERATING = 60 * 1000;

const SSE_ENABLED = true;

class SimbotReport extends React.Component {
  static displayName = 'SimbotReport';

  static propTypes = {
    params: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
    notify: PropTypes.bool.isRequired,
    slowMode: PropTypes.bool.isRequired,
    isPatron: PropTypes.bool.isRequired,
    userRewards: PropTypes.object.isRequired,
    preferEquipped: PropTypes.bool.isRequired,
    detailedProgress: PropTypes.bool.isRequired,

    onFailedJob: PropTypes.func.isRequired,
    cancelSim: PropTypes.func.isRequired,
    onClickPreferEquipped: PropTypes.func.isRequired,

    router: routerShape.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      jobData: null,
      logOpen: true,

      report: null,
      simcOutput: '',

      usingSse: false,
      sseStarted: false,
      progressInfo: null,
      log: null,
      jobState: 'inactive',

      loading: true,
      processing: false,
      notFound: false,
      error: null,

      notificationFired: false,

      measure: Measures.dps.field,

      avatarFailure: false,

      droptimizerLoaded: false,
    };

    this.lastFetchProgress = null;

    this.timeoutGenerating = null;
    this.timeoutFetch = null;
    this.timeoutFetchProgress = null;
    this.timeoutEventSourceActivity = null;

    this.getProgressSource = null;
    this.getReportSource = null;

    this.eventSource = null;
  }

  async componentDidMount() {
    this.fetchData();

    this.setState({ droptimizerLoaded: false });
    try {
      // Still needed as some calls still depend on encounter/intance data that isn't handled correctly
      // Maybe worth looking into figuring out where item/instance libraries are not being used
      // Likely related to EncounterOveride lookup
      await droptimizerLoad();
      this.setState({ droptimizerLoaded: true });
    } catch (error) {
      this.setState({ droptimizerLoaded: true, error: 'client_error' });
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.params.simId !== this.props.params.simId) {
      this.cleanup();

      this.setState({
        jobData: null,
        logOpen: true,

        usingSse: false,
        sseStarted: false,
        progressInfo: null,
        log: null,
        jobState: 'inactive',

        report: null,
        loading: true,
        processing: false,
        notFound: false,
        generateError: false,
      }, this.fetchData);

      this.lastFetchProgress = null;
    } else if (prevProps.detailedProgress !== this.props.detailedProgress) {
      // just do a full reload on changing this setting to stop/start SSE
      setTimeout(() => window.location.reload(), 250);
    }
  }

  componentWillUnmount() {
    this.cleanup();
  }

  cleanup = (cancelProgress = true) => {
    if (this.timeoutFetchProgress) {
      clearTimeout(this.timeoutFetchProgress);
    }
    if (this.timeoutGenerating) {
      clearTimeout(this.timeoutGenerating);
    }
    if (cancelProgress && this.getProgressSource) {
      this.getProgressSource.cancel('Cleanup');
    }
    if (this.getReportSource) {
      this.getReportSource.cancel('Cleanup');
    }
    if (this.eventSource && this.eventSource.readyState !== 2) {
      this.eventSource.close();
    }
    if (this.timeoutEventSourceActivity) {
      clearTimeout(this.timeoutEventSourceActivity);
    }
  };

  getMeasurePath = (measure, field) => `collected_data.${Measures[measure].field}.${field}`;

  getSortedActors = (
    actors,
    combinations,
    droptimizerItems,
    droptimizerInstances,
    baseTalents,
    source,
    measure,
    talentLoadoutNames,
  ) => {
    const baseActor = actors[0];
    let sorted = _.sortBy(
      _.filter(_.map(actors, (actor, idx) => {
        const origIdx = idx;
        const matches = actor.name.match(/Combo ([0-9]+)/);
        let comboIdx = 0;
        if (matches) {
          comboIdx = parseInt(matches[1], 10) - 1;
        }
        let equipped;
        let combo;
        if (combinations) {
          combo = combinations[comboIdx];
          const gearEquipped = _.reduce(
            combo.gear,
            (result, item, slot) => {
              const equippedItem = baseActor.simcGear[slot];
              // last check is expensive but should only be required in the case of
              // double on use trinkets being swapped around.
              return result && item && Boolean(item.equipped) && _.isEqual(item, equippedItem);
            },
            true,
          );
          if (gearEquipped) {
            if (baseTalents && combo.talents) {
              const sameTalents = baseTalents === combo.talents;

              equipped = sameTalents;
            } else {
              equipped = true;
            }
          }
        }
        let droptimizerItem;
        if (droptimizerItems) {
          const actorNameParts = _.split(actor.name, '/');
          let [
            instanceIdStr,
            encounterIdStr,
            difficultyId,
            itemIdStr,
            // eslint-disable-next-line prefer-const
            itemLevelStr,
            enchantIdStr,
            slot,
            // eslint-disable-next-line prefer-const
            bonusVariationName,
            // eslint-disable-next-line prefer-const
            craftedStatName,
            variationName,
          ] = actorNameParts;

          if (actorNameParts.length === 9) {
            // changed the format in Mar 2025 to add craftedStatName
            [
              instanceIdStr,
              encounterIdStr,
              difficultyId,
              itemIdStr,
              // eslint-disable-next-line prefer-const
              itemLevelStr,
              enchantIdStr,
              slot,
              // eslint-disable-next-line prefer-const
              bonusVariationName,
              variationName,
            ] = actorNameParts;
          } else if (actorNameParts.length === 8) {
            // changed the format in Nov 2024 to add bonusVariationNames
            [
              instanceIdStr,
              encounterIdStr,
              difficultyId,
              itemIdStr,
              // eslint-disable-next-line prefer-const
              itemLevelStr,
              enchantIdStr,
              slot,
              variationName,
            ] = actorNameParts;
          } else if (actorNameParts.length === 7) {
            // older format, before May 2023
            [
              instanceIdStr,
              encounterIdStr,
              difficultyId,
              itemIdStr,
              enchantIdStr,
              slot,
              variationName,
            ] = actorNameParts;
          }
          const instanceId = parseInt(instanceIdStr, 10);
          const encounterId = parseInt(encounterIdStr, 10);
          const id = parseInt(itemIdStr, 10);
          const itemLevel = parseInt(itemLevelStr, 10) || null;
          const enchantId = parseInt(enchantIdStr, 10);
          const craftedStat = _.find(CRAFTED_STAT_COMBOS, { value: craftedStatName });

          if (id) {
            droptimizerItem = {};

            const search = _.pickBy({
              id,
              encounterId,
              itemLevel,
              bonusVariationName,
              crafted_stats: craftedStat?.id,
            });

            let dItem = _.find(droptimizerItems, search);

            if (!dItem) {
              // older format, before Mar 2025
              dItem = _.find(droptimizerItems, { id, encounterId, itemLevel, bonusVariationName });
            }
            if (!dItem) {
              // older format, before Nov 2024
              dItem = _.find(droptimizerItems, { id, encounterId, itemLevel });
            }
            if (!dItem) {
              // older format, before May 2023
              dItem = _.find(droptimizerItems, { id, encounterId });
            }
            const variation = (
              _.find(dItem.variations, { slot, variationName })
              || _.find(dItem.variations, { slot })
            );
            droptimizerItem.item = _.cloneDeep(dItem);
            droptimizerItem.variations = dItem.variations;
            droptimizerItem.bonusVariationName = dItem.bonusVariationName;
            droptimizerItem.craftedStatName = dItem.craftedStatName;

            if (!droptimizerItem.item) {
              return null;
            }
            droptimizerItem.item.enchant_id = enchantId;
            if (droptimizerItem.item.tooltipParams) {
              droptimizerItem.item.tooltipParams.enchant = enchantId;
            }

            droptimizerItem.itemLevel = itemLevel || droptimizerItem.item.itemLevel;
            droptimizerItem.slot = slot;
            droptimizerItem.difficulty = _.find(DIFFICULTIES, { id: difficultyId });
            droptimizerItem.instance = _.find(droptimizerInstances, { id: instanceId });
            droptimizerItem.encounter = _.find(
              droptimizerItem.instance.encounters,
              { id: _.find(droptimizerItem.item.sources, { instanceId }).encounterId || encounterId },
            );

            if (variation?.alternate) {
              droptimizerItem.alternate = variation.alternate;
              droptimizerItem.alternateSlot = variation.alternateSlot;
            }
          } else {
            equipped = true;
          }
        }
        const talentsName = talentLoadoutNames[combo?.talents];
        return {
          ...actor,
          gear: _.get(combo, 'gear') || actor.gear,
          talents: combo?.talents,
          talentsName,
          origIdx,
          comboIdx,
          baseActor: idx === 0,
          combo,
          droptimizerItem,
          equipped,
        };
      })), // maintain original index
      this.getMeasurePath(measure, 'mean'),
    );

    if (Measures[measure].sort === 'desc') {
      sorted = _.reverse(sorted);
    }

    return sorted;
  };

  updateEventSourceActivity = () => {
    if (this.timeoutEventSourceActivity) {
      clearTimeout(this.timeoutEventSourceActivity);
    }
    const fn = () => {
      if (this.eventSource) {
        this.eventSource.close();
      }
      this.setupEventSource();
    };
    this.timeoutEventSourceActivity = setTimeout(fn, 30 * 1000);
  };

  setupEventSource = () => {
    const { simId } = this.props.params;
    if (!simId) {
      return;
    }
    this.eventSource = new EventSource(`/api/job/events/${simId}`);
    this.updateEventSourceActivity();
    this.eventSource.addEventListener('log', event => {
      this.updateEventSourceActivity();
      const data = JSON.parse(event.data);
      this.setState(prevState => ({
        ...prevState,
        sseStarted: true,
        jobState: 'active',
        log: _.takeRight((
          prevState.log
            ? [
              ...prevState.log,
              data,
            ]
            : [data]
        ), 10),
      }));
    });
    this.eventSource.addEventListener('progress', event => {
      this.updateEventSourceActivity();
      const data = JSON.parse(event.data);
      this.setState(prevState => ({
        ...prevState,
        sseStarted: true,
        jobState: 'active',
        progressInfo: data,
      }));
    });
    this.eventSource.addEventListener('status', event => {
      this.updateEventSourceActivity();
      const data = JSON.parse(event.data);
      switch (data) {
        case 'flight done':
          this.setState(prevState => ({
            ...prevState,
            sseStarted: true,
            jobState: 'complete',
          }));
          this.onProgressComplete();
          this.eventSource.close();
          // just in case this.cleanup() doesn't run for some reason
          if (this.timeoutEventSourceActivity) {
            clearTimeout(this.timeoutEventSourceActivity);
          }
          break;
        default:
          break;
      }
    });
    this.eventSource.addEventListener('heartbeat', () => {
      this.updateEventSourceActivity();
    });
  };

  fetchData = () => {
    // break out of the loop if there was a report generation error
    if (this.state.generateError) {
      return;
    }

    const delay = this.state.loading ? 0 : 1000;

    this.timeoutFetch = setTimeout(() => {
      this.fetchReportData().then((report) => {
        if (!report) {
          // don't call immediately as that can result in really spammy flapping
          // between looking up report data and progress for a complete job
          this.fetchProgress();

          if (SSE_ENABLED && EventSource && this.props.detailedProgress) {
            this.setState(prevState => ({ ...prevState, usingSse: true }));
            this.setupEventSource();
          }
        } else {
          this.setState({ report, loading: false, processing: false });
          clearTimeout(this.timeoutGenerating);
          // load output for error screen
          if (report.error) {
            this.fetchSimcOutput().then(output => {
              this.setState({ simcOutput: output });
            });
          }
        }
      }).catch((err) => derror(err)); // eslint-disable-line
    }, delay);
  };

  onProgressComplete = () => {
    this.cleanup();

    this.fetchData();
    if (!this.timeoutGenerating) {
      this.timeoutGenerating = setTimeout(() => {
        clearTimeout(this.timeoutFetch);
        this.setState({
          generateError: true,
          processing: false,
          loading: false,
        });
      }, TIMEOUT_GENERATING);
    }
    if (this.props.notify && !this.state.notificationFired) {
      if (window.Notification) {
        const job = _.get(this.state, 'jobData.job');
        const player = _.startCase(_.get(job, 'data.meta.player'));
        const simTitle = _.get(job, 'data.publicTitle') || _.get(job, 'data.meta.title');
        const simId = _.get(job, 'data.simId');
        const body = _.join(_.filter([player, simTitle, simId]), ' - ');
        try {
          const completeNotification = new window.Notification(
            'Sim Complete!',
            {
              body,
              icon: '/apple-touch-icon.png',
              badge: '/apple-touch-icon.png',
              timestamp: new Date(),
            },
          );
          // focus raidbots and close notification on click
          completeNotification.onclick = function onCompleteNotifyClick() { window.focus(); };

          this.setState({ notificationFired: true });
        } catch (error) {
          if (error.name !== 'TypeError') {
            Sentry.captureException(`Error creating report notification: ${error.message}`);
          }
        }
      }
    }
  };

  fetchProgress = () => {
    const { simId } = this.props.params;

    this.getProgressSource = axios.CancelToken.source();

    let params;
    let refreshTime = this.props.isPatron ? 1000 : 2000;
    if (this.props.slowMode) {
      refreshTime *= 5;
    }
    // if SSE is active, reduce frequency of XHR call as it's just a backup process
    if (this.state.sseStarted) {
      refreshTime = 15000;
      params = {
        noLog: 1,
      };
    }

    // detect if last fetchProgress was a while ago
    // Main case: browser is suspended in the middle of a sim and use wakes up device later
    // e.g., user starts a sim on their phone, sleeps their phone, wakes up phone after sim is done and kue job has been
    // deleted
    if (this.lastFetchProgress !== null) {
      const timeSinceLast = Date.now() - this.lastFetchProgress;
      // use 5 times the expected timeout to provide a bunch of wiggle room
      // I imagine this number will come back to haunt me
      if (timeSinceLast > 5 * refreshTime) {
        window.location.reload();
      }
    }

    const statusStart = Date.now();
    return axios.get(`/api/job/${simId}`, {
      params,
      cancelToken: this.getProgressSource.token,
      timeout: 15 * 1000,
      numRetries: 5,
      retryDelay: 100,
    }).then((res) => {
      const jobData = res.data;
      const { job } = jobData;
      const state = job ? job.state : null;

      this.setState(prevState => ({
        ...prevState,
        jobData,
        log: prevState.usingSse ? prevState.log : _.map(jobData.log, _.trim),
        loading: false,
        processing: true,
        jobState: this.sseStarted ? prevState.jobState : state,
      }));

      if (state === 'complete') {
        this.onProgressComplete();
      } else if (state === 'failed' && jobData.retriesRemaining <= 0) {
        // only handle full fail if there are no retries left
        clearTimeout(this.timeoutGenerating);
        this.props.onFailedJob(simId);
        // fetch full log
        axios.get(`/api/job/${simId}?full=1`).then(fullRes => {
          this.setState({ jobData: fullRes.data, loading: false });
        });
        // remove from store
      } else {
        // keep track of last successful call for sleep/wake detection on next call
        this.lastFetchProgress = Date.now();

        const statusDuration = Date.now() - statusStart;
        // schedule call to try to stick to refreshTime. So if job status takes 100ms, it'll wait 900ms
        // If it takes over 1 second, it will call again immediately
        this.timeoutFetchProgress = setTimeout(this.fetchProgress, _.max([1, refreshTime - statusDuration]));
      }
    }).catch((error) => {
      if (axios.isCancel(error)) {
        // this page cancelled the request, nothing to do
      } else {
        const status = _.get(error, 'response.status');

        // no report or simjobmap is gone
        if (status === 404) {
          this.setState({ notFound: true, processing: false });
        } else if (error.response) {
          // server problems (redis, web servers, etc)
          this.setState({ error: 'server_error', processing: false });
        } else if (error.request) {
          // client or other problems (timeout)
          this.setState({ error: 'client_error', processing: false });
        } else {
          // :shrug:
          this.setState({ error: 'unknown_error', processing: false });
          Sentry.captureException(error);
        }
        this.cleanup();
      }
    });
  };

  fetchReportData = () => {
    if (this.getProgressSource) {
      this.getProgressSource.cancel();
    }

    this.getReportSource = axios.CancelToken.source();
    const dataUrl = `${reportBaseUrl(this.props.params.simId)}/data.json`;
    return axios.get(dataUrl, { cancelToken: this.getReportSource.token })
      .then(response => response.data)
      .catch(() => false);
  };

  fetchSimcOutput = () => {
    const dataUrl = `${reportBaseUrl(this.props.params.simId)}/output.txt`;
    return axios.get(dataUrl)
      .then(response => response.data)
      .catch(() => false);
  };

  /*
  changeMeasure = (measure) => {
    this.setState({ measure });
  };
  */

  toggleLogOpen = () => {
    const { logOpen } = this.state;
    this.setState({ logOpen: !logOpen });
  };

  render() {
    const { router, preferEquipped, onClickPreferEquipped } = this.props;
    const { simId } = this.props.params;

    if (this.state.generateError) {
      return (
        <Box>
          <Heading>Error Generating Report</Heading>
          <Text>Unable to load the report!</Text>
          <Text>
            Try again in a few minutes and/or
            check Raidbots Discord to see if the site is having problems.
          </Text>
        </Box>
      );
    }

    if (this.state.error) {
      /*
       * Possible error conditions:
       *  Server problems:
       *    Persistent Redis communication problems
       *    Persistent Web servers down
       *    Redis key eviction
       *  Client problems:
       *    Very slow/spotty connection
       *  code error - fetchProgress still running even after job complete/job cleanup
       *  Manual admin cancel of job
      */
      const messages = {
        server_error: 'Raidbots servers may be having issues',
        client_error: 'Your device is having issues talking to Raidbots servers',
        unknown_error: 'Whatever happened was beyond Seriallos\' imagination and you should blame him',
        droptimizer_load_fail: 'Unable to load data due to a network issue',
      };
      return (
        <DefaultError
          title="Error Retrieving Simulation"
          description={(
            <Box>
              <Text bold size={18}>
                {messages[this.state.error] || messages.unknown_error}
              </Text>
              <Text mt={2}>
                You should try reloading the page in case this is an intermittent problem.
              </Text>
            </Box>
          )}
        />
      );
    }
    if (this.state.notFound) {
      return (
        <Flex justify="center" mt={4}>
          <Box col={6}>
            <Heading>Simulation not found</Heading>
            <Text mt={1}>Possible reasons for this message:</Text>
            <ul>
              <li><Text>Invalid URL</Text></li>
              <li><Text>Old sim has been removed</Text></li>
            </ul>
          </Box>
        </Flex>
      );
    }

    if (this.state.report) {
      const { report, measure: stateMeasure } = this.state;
      const { sim, simbot } = report;
      const rawFormData = _.get(simbot, 'meta.rawFormData');
      const simType = _.get(simbot, 'meta.type');

      let measure = stateMeasure;

      /*
      const analyzeJson = (data, overallTotal) => {
        const total = JSON.stringify(data).length;
        const result = _.mapValues(data, (value) => {
          const size = JSON.stringify(value).length;
          return {
            size,
            pct: _.round(100 * (size / total), 1),
            totalPct: overallTotal ? _.round(100 * (size / overallTotal), 1) : undefined,
          };
        });
        console.table(result);
      };

      const overallTotal = JSON.stringify(report).length;

      analyzeJson(report, overallTotal);
      analyzeJson(report.simbot.meta.rawFormData, overallTotal);
      analyzeJson(report.simbot.meta.rawFormData.optimize, overallTotal);
      */

      if (report.error || !report.sim) {
        return (
          <ReportError
            report={report}
            output={this.state.simcOutput}
          />
        );
      }

      if (simType === 'droptimizer' && !this.state.droptimizerLoaded) {
        return (
          <Flex align="center" justify="center" style={{ minHeight: 200 }}>
            <Box>
              <Loader
                delay={300}
              />
            </Box>
          </Flex>
        );
      }

      // check input for an explicit profileset_metric option
      if (simbot.meta.measure) {
        measure = Measures[simbot.meta.measure].field;
      }

      let actors = unionActors(report);

      const actorTypes = _.mapValues(_.groupBy(actors, 'actorType'), as => as.length);

      const isRaidProfileset = (actorTypes.copy > 1 && actorTypes.profileset > 0) || simbot.meta.measure === 'raid_dps';
      if (isRaidProfileset) {
        actors = _.filter(actors, actor => actor.baseActor || actor.actorType === 'profileset');
        measure = sim.options.profileset_metric || Measures.raid_dps.field;
      }

      /*
      const availableMeasures = _.filter(['dps', 'prioritydps'], metric => {
        return _.every(actors, `collected_data.${metric}`);
      });
      */

      const player = actors[0];
      const basePlayer = _.get(sim, 'players[0]', {});
      const numActors = actors.length;

      const tank = isTank(_.get(rawFormData, 'character'));
      const healer = isHealer(_.get(rawFormData, 'character'));

      const isSmart = _.get(simbot, 'smart', false);
      const isMultistage = _.size(simbot?.flightmaster?.stages) > 1;
      let isHighError = false;
      const maxActorErrorInfo = getSimErrorInfo(report);
      if (!isSmart) {
        isHighError = maxActorErrorInfo.percent > 0.001;
      }

      const actionSequence = _.get(basePlayer, 'collected_data.action_sequence');
      const actionSequencePrecombat = _.get(basePlayer, 'collected_data.action_sequence_precombat');

      const showDetailedStats = numActors === 1 && _.includes(['quick', 'advanced'], simbot.meta.type);

      //const isPtr = _.get(sim, 'options.dbc.version_used') === 'PTR';
      // eslint-disable-next-line camelcase
      const isPtr = _.some(sim.players, p => p.dbc?.version_used === 'PTR');

      const isExpertMode = Boolean(simType !== 'advanced' && (
        rawFormData?.expertHeader
        || rawFormData?.expertFooter
        || rawFormData?.expertPreActors
        || rawFormData?.expertRaidActors
        || rawFormData?.expertPostActors
      ));

      const scaleFactors = _.get(player, 'scale_factors_all.dps') || _.get(player, 'scale_factors');

      const character = _.get(simbot, 'meta.rawFormData.character');
      const region = _.get(character, 'region') || _.get(simbot, 'meta.rawFormData.armory.region');
      const characterLinks = getCharacterLinks(region, character);

      const optimize = _.get(rawFormData, 'optimize');

      const talentInfo = getTalentInfo(report);

      const avatarUrl = avatar(region, character);

      let equippedGear = getEquippedGear(report);
      if (simType === 'droptimizer') {
        // Droptimizer has the "upgrade equipped" option so use Droptimizer's data for equipped gear
        // Just consider this another hack on top of the upgradeEquipped pile of hacks
        equippedGear = rawFormData.droptimizer.equipped;
      }
      const avgItemLevel = getAvgItemLevel(report);

      const classColor = config.colors.class[_.get(simbot, 'meta.charClass', '')?.replace(' ', '')];

      const maxMeasure = getMaxMeasure(report, measure);

      const raceInfo = _.find(races, { simcName: basePlayer.race });

      const fightStyleInfo = _.find(
        fightStyles,
        { value: _.get(simbot, 'meta.fightStyle') || _.get(sim, 'options.fight_style') || sim.fight_style },
      );

      const reportDate = simbot.date ? new Date(simbot.date) : undefined;

      const specInfo = getCharacterSpecInfo(character);
      let addSocketGem;
      if (specInfo && optimize) {
        const addSocketGemId = _.get(_.find(optimize.addSocketGem, { specId: specInfo.id }), 'gemId');
        const addSocketNum = optimize.addSocketNum;
        addSocketGem = addSocketNum && addSocketGemId;
      }

      const typeMap = {
        stats: {
          name: 'Stat Weights',
          link: 'stats',
        },
        optimize: {
          name: 'Top Gear',
          link: 'topgear',
        },
        droptimizer: {
          name: 'Droptimizer',
          link: 'droptimizer',
        },
        gear: {
          name: 'Gear Compare',
          link: 'gear',
        },
        talents: {
          name: 'Talent Compare',
          link: 'talents',
        },
        quick: {
          name: 'Quick Sim',
          link: 'quick',
        },
        advanced: {
          name: 'Advanced',
          link: 'advanced',
        },
      };

      const backLink = typeMap[simbot.meta.type];

      // map combinations simple array back into full gear data
      let combinations = _.get(rawFormData, 'optimize.combinations', null);
      const resolveCombos = combinations &&
        (
          _.isArray(_.first(combinations)) // intermediate sparse array format
          || (
            _.isObject(combinations)
            && _.isArray(combinations[_.keys(combinations)[0]]) // make sure this is one of the newer formats
          )
          || (
            // make sure this is the newest format with gear/talents
            _.isObject(combinations)
            && _.has(combinations[_.keys(combinations)[0]], 'gear')
          )
          // there's an older format that is an object of gear objects, this was the original HUGE version
        );
      if (resolveCombos) {
        const newCombos = {};
        const slots = [
          'head', 'neck', 'shoulder', 'back', 'chest', 'wrist', 'hands', 'waist',
          'legs', 'feet', 'finger1', 'finger2', 'trinket1', 'trinket2', 'main_hand', 'off_hand',
        ];
        const gearByOrigIdx = _.mapValues(rawFormData.optimize.gear, gear => {
          return _.keyBy(gear, 'origIdx');
        });
        const allGearByOrigIdx = _.mapValues(rawFormData.optimize.allGear, gear => {
          return _.keyBy(gear, 'origIdx');
        });
        _.each(combinations, (combo, comboIdx) => {
          if (!combo) {
            return;
          }
          newCombos[comboIdx] = {
            gear: _.fromPairs(
              _.filter(
                _.map(
                  slots,
                  (slot, slotIdx) => {
                    let slotName;
                    if (_.includes(['finger1', 'finger2'], slot)) {
                      slotName = 'rings';
                    } else if (_.includes(['trinket1', 'trinket2'], slot)) {
                      slotName = 'trinkets';
                    } else if (gearByOrigIdx.weapons && _.includes(['main_hand', 'off_hand'], slot)) {
                      // bit of backwards compatibility for interim weapon data structure. current reports
                      // have main_hand and off_hand separated
                      slotName = 'weapons';
                    } else {
                      slotName = slot;
                    }

                    // resolve multiple formats of handing gear/combos over time
                    // * combo.allGear
                    // * combo.gear
                    // * comba array

                    let comboSlotIdx;
                    let origItem;
                    if (_.has(combo, 'gear')) {
                      comboSlotIdx = combo.gear[slotIdx];
                      // if gear v3 and base actor (equipped), look up allgear
                      if (_.has(rawFormData, 'optimize.allGear') && (comboIdx === 0 || comboIdx === '0')) {
                        // gear v3
                        origItem = _.get(allGearByOrigIdx, `[${slotName}][${comboSlotIdx}]`);
                      } else {
                        // gear v2 and non-equipped v3
                        origItem = _.get(gearByOrigIdx, `[${slotName}][${comboSlotIdx}]`);
                      }
                    } else {
                      // gear v1
                      comboSlotIdx = combo[slotIdx];
                      origItem = _.get(gearByOrigIdx, `[${slotName}][${comboSlotIdx}]`);
                    }

                    if (origItem) {
                      let item;
                      if (combo.overrides) {
                        item = origItem;
                        const slotOverrides = _.find(combo.overrides, { slot });
                        if (slotOverrides) {
                          const enchantOverride = slotOverrides.override.enchant_id;
                          const gemOverride = slotOverrides.override.gem_id;
                          const addSocket = slotOverrides.override.addSocket;
                          let enchantDiff;
                          if (enchantOverride) {
                            enchantDiff = parseInt(item.enchant_id, 10) !== parseInt(enchantOverride, 10);
                          }
                          let gemDiff;
                          if (gemOverride) {
                            gemDiff = item.gem_id !== gemOverride;
                          }
                          let bonusIds = item.bonus_id;
                          if (addSocket) {
                            const bids = _.split(bonusIds, '/');
                            bids.push(1808);
                            bonusIds = _.join(bids, '/');
                          }
                          item = {
                            ...item,
                            ...slotOverrides.override,
                            bonus_id: bonusIds,
                            equipped: item.equipped && !enchantDiff && !gemDiff && !item.customItem,
                            originalIsEquipped: item.equipped,
                            enchantDiff,
                            gemDiff,
                          };
                        }
                      } else if (origItem.customItem) {
                        item = {
                          ...origItem,
                          equipped: false,
                          // TODO: This is probably a hack?
                          originalIsEquipped: false,
                        };
                      } else {
                        item = origItem;
                      }
                      return [slot, item];
                    }
                    return null;
                  },
                ),
              ),
            ),
            talents: combo.talents,
            overrides: combo.overrides,
          };
          combinations = newCombos;
        });
      }

      let droptimizerItems;
      let droptimizerInstances;
      const droptimizerOptions = _.get(rawFormData, 'droptimizer', null);
      try {
        if (simType === 'droptimizer' && droptimizerOptions) {
          if (simbot.meta.itemLibrary) {
            droptimizerItems = simbot.meta.itemLibrary;
          } else {
            droptimizerItems = Droptimizer.getItems(droptimizerOptions);
          }
          if (simbot.meta.instanceLibrary) {
            droptimizerInstances = simbot.meta.instanceLibrary;
          } else {
            droptimizerInstances = GameData.Instances;
          }
          const variations = Droptimizer.getVariations({
            ...droptimizerOptions,
            itemLibrary: simbot.meta.encounterItems || droptimizerItems,
          });
          droptimizerItems = _.map(droptimizerItems, dItem => ({
            ...dItem,
            variations: _.filter(variations, { item: { id: dItem.id } }),
          }));
        }
      } catch (droptimizerError) {
        if (droptimizerError.message === 'instance_not_found') {
          // seems like this is fine at least on an expansion threshold. some reports throw this because old sources
          // have been purged but the page works fine if this is caught and processing continues
        } else {
          throw droptimizerError;
        }
      }

      // collect known talent names
      const talentLoadoutNames = {};
      _.each(character?.talentLoadouts, loadout => {
        talentLoadoutNames[loadout.string] = loadout.name;
      });

      // these will overwrite what's on the character - but that should be fine because they were built from the
      // character originally
      if (optimize?.talentLoadouts) {
        _.each(optimize.talentLoadouts, loadout => {
          talentLoadoutNames[loadout.string] = loadout.name;
        });
      }

      const source = _.get(rawFormData, 'optimize.source', '');
      const sortedActors = this.getSortedActors(
        actors,
        combinations,
        droptimizerItems,
        droptimizerInstances,
        TalentTraits.stringify(talentInfo.talents),
        source,
        measure,
        talentLoadoutNames,
      );

      //--------------------------------------
      // find the top actor
      //
      // by default, use the top result
      // BUT check equipped to see if it's within a small threshold and use that instead

      const topActor = sortedActors[0];
      let focusActor = topActor;
      const equippedActor = _.find(sortedActors, { equipped: true });

      if (preferEquipped && equippedActor) {
        const topDps = _.get(topActor, this.getMeasurePath(measure, 'mean'));
        const equippedDps = _.get(equippedActor, this.getMeasurePath(measure, 'mean'));

        const dpsDiff = topDps - equippedDps;
        if (dpsDiff < (2 * maxActorErrorInfo.error)) {
          focusActor = equippedActor;
        }
      }

      let showCopyButton = false;
      if (_.includes(['gear', 'talents', 'optimize', 'droptimizer'], simType)) {
        if (simType === 'optimize') {
          // check for proper properties in data.json to show the button
          // test property is the "selected" data - this is newly added to JSON as of Sept 2018
          showCopyButton = Boolean(_.get(optimize, 'selected'));
        } else {
          // show for all gear, talent sims
          showCopyButton = true;
        }
      }

      let enableActorCollapse = false;
      if (simType === 'droptimizer') {
        enableActorCollapse = true;
      }
      if (simType === 'optimize') {
        // check for a "mixed" sim
        enableActorCollapse = true;
      }

      const headerGearIconSize = 34;
      const headerExtraIconSize = 20;

      const styleStripes = (color1, color2) => (
        `repeating-linear-gradient(-45deg, ${color1}, ${color1} 10px, ${color2} 10px, ${color2} 20px)`
      );
      const expertWarningStyles = {
        background: styleStripes('#292929', '#493900'),
        margin: -20,
        padding: 20,
      };

      // Should we show IQD warning
      let showHighSecondaryProcWarning = false;
      if (equippedActor) {
        const gear = equippedActor.gear || equippedActor.simcGear;
        if (gear) {
          const hspEquipped = _.some(
            [gear.trinket1?.id, gear.trinket2?.id],
            isHighSecondaryProcTrinket,
          );

          const secondaries = _.pick(
            equippedActor.stats,
            ['crit_rating', 'haste_rating', 'mastery_rating', 'versatility_rating'],
          );

          const secondaryValues = _.reverse(_.sortBy(secondaries));
          const topStatDiff = secondaryValues[0] - secondaryValues[1];
          const topStatsClose = topStatDiff < 100;

          showHighSecondaryProcWarning = hspEquipped && topStatsClose;
        }
      }

      let showBreathOfNeltharionWarning = false;
      if (equippedActor) {
        _.each(equippedActor.gear || equippedActor.simcGear, item => {
          if (_.includes(item?.gem_id, '198297')) {
            showBreathOfNeltharionWarning = true;
          }
        });
      }

      const invalidArmor = getIncorrectArmorPieces(character);
      const wrongMainStatPieces = getIncorrectMainStatPieces(character);

      // check the JSON for the flag
      let unsupportedFightStyle = _.some(sim.players, p => p.valid_fight_style === false);
      // double check notifications, remove if present
      const notifications = _.filter(report.notifications, notification => {
        if (notification.match(/does not support fight style/)) {
          unsupportedFightStyle = true;
          return false;
        }
        return true;
      });

      const hasNotifications = notifications.length > 0;

      const isUnverifiedAddonSource = simbot?.meta?.addonInfo?.validVersion === false;
      const isInvalidAddonChecksum = simbot?.meta?.addonInfo?.invalidChecksum === true;

      const preDragonflightSim = report.version === '920-01';

      if (preDragonflightSim) {
        // Run Sim Again doesn't work for Shadowlands reports, hide the button
        showCopyButton = false;
      }

      const showDoubleOnUseWarning = simType === 'droptimizer' && hasDoubleOnUseEquipped(character);

      let showTempEnchantDualWarning = false;
      const tempEnchant = basePlayer.temporary_enchant;
      if (specInfo && tempEnchant) {
        const runeOpts = findExpacOption('temporaryEnchant');
        const runeOpt = _.find(runeOpts.values, { value: tempEnchant });
        if (runeOpt?.dualWield) {
          showTempEnchantDualWarning = !specInfo.dualWield;
        }
      }

      return (
        <Box style={isExpertMode ? expertWarningStyles : undefined} p={0}>
          <Helmet title={getReportTitleFromJson(report)} />

          <If
            condition={backLink}
            then={() => (
              <Box mb={1}>
                <Link
                  id="Report-backLink"
                  to={`/simbot/${backLink.link}`}
                  href={`/simbot/${backLink.link}`}
                >
                  &laquo; Back to {backLink.name}
                </Link>
              </Box>
            )}
          />

          <If condition={isExpertMode}>
            <Message theme="error" style={{ display: '', padding: 7, marginTop: 6, marginBottom: 10 }}>
              <Text align="center">
                SimC Expert Mode was enabled for this sim. Analyze with care!
                {' '}
                <a
                  href="https://support.raidbots.com/article/15-simc-expert-mode"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: config.colors.white,
                    textDecoration: 'underline',
                  }}
                >
                  More info
                </a>
              </Text>
            </Message>
          </If>

          <Flex justify="space-between" align="flex-start" wrap>
            <Box lg={9} sm={12} pr={2}>
              <DarkerBox
                mb={2}
                py={2}
              >
                <Responsive size="small">
                  {(isSmall) => (
                    <>
                      <Flex wrap={isSmall} align="flex-start">

                        {/* avatar */}

                        <If condition={avatarUrl}>
                          <Media
                            img={this.state.avatarFailure ? null : avatarUrl}
                            pl={1}
                            style={{
                              borderLeft: `5px solid ${classColor}`,
                              height: 116,
                              width: 265,
                            }}
                            mb={0}
                            onImageError={() => this.setState({ avatarFailure: true })}
                          />
                        </If>

                        {/* Name, DPS, etc*/}

                        <Box lg={6}>
                          <Heading
                            level={1}
                            style={{
                              textTransform: '',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {uppercaseName(_.get(simbot, 'meta.player', player.name))}
                          </Heading>
                          <If
                            // DPS value was confusing folks on Droptimizer reports so hide it
                            condition={simType !== 'droptimizer'}
                            then={() => (
                              <Heading level={2}>
                                {formatDps(maxMeasure)}
                                {' '}
                                {Measures[measure].display}
                              </Heading>
                            )}
                          />
                          <Text size={15}>
                            {_.get(raceInfo, 'name') || _.startCase(basePlayer.race)} {basePlayer.specialization}
                          </Text>
                          <Text size={15}>
                            {simbot.meta.title}
                          </Text>
                        </Box>

                        <Space auto />

                        {/* character links and Run Again button */}

                        <Box lg={2}>
                          <Flex column align="center" justify="space-between" wrap>
                            <If
                              condition={character && characterLinks}
                              then={() => (
                                <Flex align="center">
                                  <Box mr={2}>
                                    <a
                                      href={characterLinks.armory}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      title={`${character.name} on WoW Armory`}
                                    >
                                      <img
                                        src="/images/icon-wow.png"
                                        height={36}
                                        align="middle"
                                        alt=""
                                      />
                                    </a>
                                  </Box>
                                  <Box mr={2}>
                                    <a
                                      href={characterLinks.warcraftlogs}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      title={`${character.name} on Warcraft Logs`}
                                    >
                                      <img
                                        src="/images/icon-wcl.png"
                                        height={36}
                                        align="middle"
                                        alt=""
                                      />
                                    </a>
                                  </Box>
                                  <Box>
                                    <a
                                      href={characterLinks.raiderio}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      title={`${character.name} on Raider.IO`}
                                    >
                                      <img
                                        src="/images/icon-raiderio.png"
                                        height={36}
                                        align="middle"
                                        alt=""
                                      />
                                    </a>
                                  </Box>
                                </Flex>
                              )}
                            />

                            <If condition={showCopyButton}>
                              <Box mt={2}>
                                <CopyTemplateButton
                                  type={simbot.meta.type}
                                  data={simbot}
                                />
                              </Box>
                            </If>
                          </Flex>
                        </Box>
                      </Flex>


                      <If condition={equippedGear}>
                        <>
                          <Divider />

                          <Flex align="center" mt={2}>
                            <Box>
                              <GearStrip
                                equippedGear={equippedGear}
                                classId={character && character.class}
                                specName={talentInfo.specName}
                                size={headerGearIconSize}
                                showEmptySlots
                                wrap
                              />
                            </Box>
                            <If condition={avgItemLevel}>
                              <Box>
                                <Badge theme="info" pill mr={1} style={{ marginBottom: '6px' }}>
                                  {avgItemLevel}
                                </Badge>
                              </Box>
                            </If>
                          </Flex>
                        </>
                      </If>

                      <Flex align="center" mt={1}>
                        <If
                          condition={talentInfo.talents && simbot.meta.type !== 'optimize'}
                          then={() => (
                            <>
                              <TalentStrip
                                talents={talentInfo.talents}
                                size={headerExtraIconSize}
                                specId={talentInfo.specId}
                                level={character?.level}
                                wrap
                              />
                              {/*
                              <TalentTree
                                talents={talentInfo.talents}
                                specId={talentInfo.specId}
                                level={character.level}
                                width={700}
                                strokeWidth={1}
                              />
                              */}
                            </>
                          )}
                        />
                      </Flex>
                    </>
                  )}
                </Responsive>
              </DarkerBox>

              <If condition={preDragonflightSim}>
                <Message theme="error" style={{ display: '', padding: 7, marginTop: 6, marginBottom: 6 }}>
                  Shadowlands reports are no longer supported. Old talents, soulbinds, conduits, etc will not be shown.
                </Message>
              </If>
              <If
                condition={unsupportedFightStyle}
                then={() => (
                  <Message theme="warning" style={{ display: '', padding: 7, marginTop: 6, marginBottom: 6 }}>
                    Unreliable fight style! The SimC {specInfo?.name} module does not support {fightStyleInfo?.name}.
                    {' '}
                    <a
                      href="https://support.raidbots.com/article/43-unreliable-fight-styles"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: config.colors.black,
                        textDecoration: 'underline',
                      }}
                    >
                      More Details
                    </a>
                  </Message>
                )}
              />
              <If condition={hasNotifications}>
                <Message theme="warning" style={{ display: '', padding: 7, marginTop: 6, marginBottom: 6 }}>
                  The sim generated some warning/error messages. Check the &quot;SimC Notifications&quot; section
                  for more details.
                </Message>
              </If>
              <If condition={character && character.level < MAX_LEVEL}>
                <Message theme="warning" style={{ display: '', padding: 7, marginTop: 6, marginBottom: 6 }}>
                  Lower level characters are not well supported. Invalid results or errors are possible.
                </Message>
              </If>
              <If condition={tank || healer}>
                <Message theme="warning" style={{ display: '', padding: 7, marginTop: 6, marginBottom: 6 }}>
                  Tanks and Healers sim DPS, <strong>not</strong> healing or survival.
                  Support varies significantly by class.
                </Message>
              </If>

              <If condition={isPtr}>
                <Message theme="warning" style={{ padding: 7, marginTop: 6, marginBottom: 6 }}>
                  PTR Simulation
                </Message>
              </If>

              <If condition={isHighError}>
                <Message theme="warning" style={{ padding: 7, marginTop: 6, marginBottom: 6 }}>
                  <Icon name="warning" size={20} style={{ marginRight: 10 }} />
                  This report has a high error margin. Results may be shaky, interpret with care.
                </Message>
              </If>
              <If condition={isUnverifiedAddonSource}>
                <Message theme="warning" style={{ padding: 7, marginTop: 6, marginBottom: 6 }}>
                  <Icon name="warning" size={20} style={{ marginRight: 10 }} />
                  Input was from an out-of-date SimC addon.
                  Raidbots tools are not guaranteed to work.
                </Message>
              </If>
              <If condition={isInvalidAddonChecksum}>
                <Message theme="warning" style={{ padding: 7, marginTop: 6, marginBottom: 6 }}>
                  <Icon name="warning" size={20} style={{ marginRight: 10 }} />
                  SimC input has been edited/modified.
                  Raidbots tools are not guaranteed to work with custom input.
                </Message>
              </If>
              <If condition={showTempEnchantDualWarning}>
                <Message theme="warning" style={{ padding: 7, marginTop: 6, marginBottom: 6 }}>
                  <Icon name="warning" size={20} style={{ marginRight: 10 }} />
                  Weapon Rune option is intended for dual wield spec and this character is not a dual wielder.
                </Message>
              </If>

              <If
                condition={showDoubleOnUseWarning}
                then={() => (
                  <Flex justify="center">
                    <Text color={config.colors.orange} size={14}>
                      Double On Use trinkets can sometimes lead to confusing Droptimizer trinket results:
                      {' '}
                      <a
                        href="https://support.raidbots.com/article/23-how-do-sims-handle-double-on-use-trinkets"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          textDecoration: 'underline',
                        }}
                      >
                        More Details
                      </a>
                    </Text>
                  </Flex>
                )}
              />

              <If
                condition={_.size(invalidArmor) > 0}
                then={() => (
                  <Box>
                    <Divider />
                    <Message theme="warning" p={1} mt={1}>
                      Character has wrong armor type equipped in some slots: {_.join(_.map(invalidArmor, 'name'), ', ')}
                    </Message>
                  </Box>
                )}
              />
              <If
                condition={_.size(wrongMainStatPieces) > 0}
                then={() => (
                  <Box>
                    <Message theme="warning" p={1} mt={1}>
                      Character has wrong main stat equipped in some slots:
                      {' '}
                      {_.join(_.map(wrongMainStatPieces, 'name'), ', ')}
                    </Message>
                  </Box>
                )}
              />
              <If condition={showHighSecondaryProcWarning}>
                <Message theme="warning" style={{ display: '', padding: 7, marginTop: 6, marginBottom: 6 }}>
                  Your top two secondary stats are very close and you have a trinket with a large secondary stat proc
                  - this can cause surprising sim results!
                  {' '}
                  <a
                    href="https://support.raidbots.com/article/18-weird-results-when-simming-iqd"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: config.colors.black,
                      textDecoration: 'underline',
                    }}
                  >
                    More Details
                  </a>
                </Message>
              </If>
              <If condition={showBreathOfNeltharionWarning}>
                <Message theme="warning" style={{ display: '', padding: 7, marginTop: 6, marginBottom: 6 }}>
                  Your Breath of Neltharion tinker may cause unexpected results (because it&apos;s pretty bad).
                  {' '}
                  <a
                    href="https://support.raidbots.com/article/45-breath-of-neltharion-tinker-is-bad"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: config.colors.black,
                      textDecoration: 'underline',
                    }}
                  >
                    More Details
                  </a>
                </Message>
              </If>

              <If
                condition={showDetailedStats && (basePlayer.stats || basePlayer.buffs || actionSequence)}
                then={() => (
                  <>
                    <Text small align="center">
                      <a
                        href="https://support.raidbots.com/article/65-detailed-stats-in-quick-sim"
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        Read more about Damage Breakdown, Buff Uptime, and Sample Ability Log
                      </a>
                    </Text>
                    <If
                      condition={basePlayer.stats}
                      then={() => (
                        <DamageBreakdown
                          stats={basePlayer.stats}
                          petStats={basePlayer.stats_pets}
                          duration={sim.options.max_time}
                        />
                      )}
                    />
                    <If
                      condition={basePlayer.buffs}
                      then={() => (
                        <BuffReport
                          buffs={basePlayer.buffs}
                          constantBuffs={basePlayer.buffs_constant}
                        />
                      )}
                    />
                    <If
                      condition={actionSequence}
                      then={() => (
                        <Box mt={3} mb={5}>
                          <ActionSequence
                            precombat={_.filter(actionSequencePrecombat, 'id')}
                            sequence={actionSequence}
                          />
                        </Box>
                      )}
                    />
                  </>
                )}
              />


              <If
                condition={simbot.meta.type === 'optimize'}
                then={() => (
                  <>
                    <TopGearPanel
                      actors={sortedActors}
                      topActor={topActor}
                      focusActor={focusActor}
                      equippedActor={equippedActor}
                      classId={character && character.class}
                      specName={talentInfo.specName}
                      baseTalents={talentInfo.talents}
                      level={character?.level}
                      preferEquipped={preferEquipped}
                      onClickPreferEquipped={onClickPreferEquipped}
                    />
                    <If
                      condition={addSocketGem}
                      then={() => {
                        const gem = _.find(getGemInfo(), { itemId: parseInt(addSocketGem, 10) });
                        return (
                          <Flex justify="center">
                            <Box my={0} py={1} px={3} style={{ borderRadius: 5, backgroundColor: '#111' }}>
                              <Flex ml={1} align="center" justify="center">
                                <WowIcon
                                  type="item"
                                  id={addSocketGem}
                                  icon={gem.itemIcon}
                                  craftingQuality={gem.craftingQuality}
                                  size={24}
                                  noLink
                                  border={`1px solid ${config.colors.blue}`}
                                />
                                <Text small ml={1} color={config.colors.secondary}>
                                  Socket added
                                </Text>
                              </Flex>
                            </Box>
                          </Flex>
                        );
                      }}
                    />
                  </>
                )}
              />
              <If
                condition={simbot.meta.type === 'droptimizer'}
                then={() => (
                  <DroptimizerPanel
                    actors={sortedActors}
                    baseGear={equippedGear}
                    droptimizerItems={droptimizerItems}
                    droptimizerOptions={droptimizerOptions}
                    classId={character && character.class}
                    specName={talentInfo.specName}
                    errorMarginPct={maxActorErrorInfo.percent}
                    itemLibrary={simbot.meta.itemLibrary}
                    instanceLibrary={simbot.meta.instanceLibrary}
                    measure={measure}
                  />
                )}
              />
              <If condition={numActors > 1}>
                <Box mt={2}>
                  <React.Suspense fallback={<FallbackLoader height={100} delay={0} />}>
                    <ActorCompare
                      actors={sortedActors}
                      topActor={topActor}
                      focusActor={focusActor}
                      measure={measure}
                      baseGear={equippedGear}
                      baseTalents={talentInfo.talents}
                      gearsets={_.get(rawFormData, 'gearsets')}
                      talentSets={_.get(rawFormData, 'talentSets')}
                      type={simbot.meta.type}
                      barColor={config.colors.class[simbot.meta.charClass]}
                      specName={talentInfo.specName}
                      source={source}
                      culledActors={_.get(simbot, 'numProfilesetsCulled', 0)}
                      fullJsonUrl={`${reportBaseUrl(simId)}/data.full.json`}
                      fullJsonName={`${simId}.json`}
                      errorMargin={maxActorErrorInfo.error}
                      enableCollapse={enableActorCollapse}
                      optimizeData={_.get(optimize, 'data')}
                      character={character}
                      isMultistage={isMultistage}
                    />
                  </React.Suspense>
                </Box>
              </If>

              <If
                condition={!_.isEmpty(scaleFactors)}
                then={() => (
                  <>
                    <Message theme="warning" p={1} mt={1}>
                      Stat Weights have several shortcomings. Direct sims (Top Gear, Droptimizer, Gear Compare)
                      are better.
                      &nbsp;
                      <a
                        href="https://support.raidbots.com/article/66-beware-of-stat-weights"
                        rel="noopener noreferrer"
                        target="_blank"
                        style={{
                          color: config.colors.white,
                          textDecoration: 'underline',
                        }}
                      >
                        Read More
                      </a>
                    </Message>
                    <Box sm={12} lg={6} mt={1}>
                      <StatWeightReport
                        player={player}
                        charClass={simbot.meta.charClass}
                        spec={simbot.meta.spec}
                        fightStyle={fightStyleInfo.name}
                        tank={tank}
                        date={reportDate}
                      />
                    </Box>
                  </>
                )}
              />

              {/*
              <p>Crit: {_.round(player.collected_data.buffed_stats.stats.crit_pct * 100, 2)}</p>
              <p>Haste: {_.round(player.collected_data.buffed_stats.stats.haste_pct * 100, 2)}</p>
              <p>Mastery: {_.round(player.collected_data.buffed_stats.stats.mastery_pct * 100, 2)}</p>
              <p>Versatility: {_.round(player.collected_data.buffed_stats.stats.versatility_pct * 100, 2)}</p>
              <p>Avoidance: {_.round(player.collected_data.buffed_stats.stats.avoidance * 100, 2)}</p>
              <p>Leech: {_.round(player.collected_data.buffed_stats.stats.leech * 100, 2)}</p>
              <p>Speed: {_.round(player.collected_data.buffed_stats.stats.speed * 100, 2)}</p>
              */}


              <If
                condition={hasNotifications}
                then={() => (
                  <>
                    <Divider my={2} />
                    <Box mt={2} mb={2}>
                      <SimcNotifications notifications={notifications} />
                    </Box>
                  </>
                )}
              />

              <Divider my={2} />

              <SimcInput simId={simId} report={report} />

              <Divider my={2} />

              <If condition={isExpertMode}>
                <Box mt={2}>
                  <Message theme="error" style={{ display: '', padding: 7, marginTop: 6, marginBottom: 10 }}>
                    <Text align="center">
                      SimC Expert Mode was enabled for this sim. Analyze with care!
                      {' '}
                      <a
                        href="https://support.raidbots.com/article/15-simc-expert-mode?preview=60adab86dca0fd46b9359222"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: config.colors.white,
                          textDecoration: 'underline',
                        }}
                      >
                        More info
                      </a>
                    </Text>
                  </Message>
                </Box>
              </If>
            </Box>

            <Box lg={3} sm={12}>
              <SimDetails
                report={report}
                simId={simId}
                errorDetails={maxActorErrorInfo}
                expertMode={isExpertMode}
                unsupportedFightStyle={unsupportedFightStyle}
              />
              <ShareReport />
              <If
                condition={DEVMODE}
                then={() => {
                  const url = new URL(window.location);
                  const liveUrl = `https://www.raidbots.com${url.pathname}`;
                  let addonInput;
                  if (report.simbot && report.simbot.input) {
                    addonInput = report?.simbot?.meta?.rawFormData?.text;
                    if (report.simbot.simType === 'optimize') {
                      addonInput = SInput.stringify(report?.simbot?.meta?.rawFormData?.optimize?.data);
                    }
                  }
                  return (
                    <>
                      <Heading level={5} mt={2}>Dev Tools</Heading>
                      <Box>
                        <a href={liveUrl} target="_blank" rel="noopener noreferrer">www link</a>
                      </Box>
                      <If
                        condition={addonInput}
                        then={() => (
                          <CopyToClipboard
                            name="Copy Original Addon Input"
                            value={addonInput}
                            tiny
                            width={200}
                          />
                        )}
                      />
                    </>
                  );
                }}
              />
            </Box>
          </Flex>
        </Box>
      );
    } if (this.state.processing) {
      let results;
      const { isPatron, userRewards, cancelSim } = this.props;
      const { jobData } = this.state;
      const { job } = jobData;
      const statusProps = {
        title: job.data.publicTitle || _.get(job, 'data.meta.title'),
        simType: job.data.simType || _.get(job, 'data.meta.type'),
        player: job.data.player || _.get(job, 'data.meta.player'),
        charClass: job.data.charClass || _.get(job, 'data.meta.charClass'),
        spec: job.data.spec || _.get(job, 'data.meta.spec'),
        fightStyle: job.data.fightStyle || _.get(job, 'data.meta.fightStyle'),
        fightLength: job.data.fightLength || job.data?.meta?.fightLength,
        enemyCount: job.data.enemyCount || job.data?.meta?.enemyCount,
        simcVersion: job.data.simcVersion || _.get(job, 'data.meta.simcVersion'),
        concurrency: job.data.concurrency,
        job: job || {},
        state: this.state.jobState,
        log: _.join(this.state.log, '\n'),
        logOpen: this.state.logOpen,
        onLogToggle: this.toggleLogOpen,
        queueSize: _.get(jobData, 'queue.total'),
        queuePosition: _.get(jobData, 'queue.position'),
        waitAvgPublic: _.get(jobData, 'queue.avgWaitPublic'),
        waitAvgPatron: _.get(jobData, 'queue.avgWaitPatron'),
        userRewards,
        isPatron,
        userConcurrency: _.get(job, 'data.userConcurrency'),
        isSwiftEligible: _.get(job, 'data.isConcurrencyEligible', false),
        numProfilesets: _.get(jobData, 'numProfilesets', 0),
        percentComplete: _.get(this.state, 'progressInfo.totalProgress') || job.progress,
        stageProgress: this.state.usingSse
          ? _.get(this.state, 'progressInfo.stageProgress')
          : _.get(job, 'progress_data.stageProgress'),
        curStage: this.state.usingSse ? _.get(this.state, 'progressInfo.curStage') : job.data.stage,
        numStages: this.state.usingSse ? _.get(this.state, 'progressInfo.numStages') : job.data.numStages,
        numActors: this.state.usingSse ? _.get(this.state, 'progressInfo.totalActors') : job.data.stageActors,
        stageTarget: job.data.stageTarget,
        slowMode: this.props.slowMode,
        onClickCancel: () => cancelSim(simId).then(() => {
          const type = _.get(job, 'data.type') || _.get(job, 'data.meta.type', 'unknown');
          const typeMap = {
            stats: 'stats',
            optimize: 'topgear',
            gear: 'gear',
            talents: 'talents',
            quick: 'quick',
            advanced: 'advanced',
            droptimizer: 'droptimizer',
          };
          router.replace(`/simbot/${typeMap[type] || ''}`);
        }),
      };
      const jobStatus = <JobStatus {...statusProps} />;
      if (this.state.jobState === 'failed') {
        results = (
          <Link to="/simbot" href="/simbot">
            Back to Simbot
          </Link>
        );
      }
      if (this.state.jobState === 'complete') {
        results = (
          <Flex align="center">
            <Box>
              <Loader
                delay={100}
              />
            </Box>
            <Box pl={1}>
              Generating report...
            </Box>
          </Flex>
        );
      }
      return (
        <Section pt={1}>
          <Helmet title="Simulating - Raidbots" />
          {jobStatus}
          {results}
        </Section>
      );
    }


    return (
      <Flex align="center" justify="center" style={{ minHeight: 200 }}>
        <Box>
          <Loader
            delay={300}
          />
        </Box>
      </Flex>
    );
  }
}

const mapStateToProps = state => ({
  isPatron: userSelectors.isPatron(state),
  userRewards: userSelectors.userRewards(state),
  notify: formSelectors.sendBrowserNotification(state),
  slowMode: siteSelectors.slowMode(state),
  preferEquipped: reportSelectors.preferEquipped(state),
  detailedProgress: reportSelectors.detailedProgress(state),
});

const mapDispatchToProps = {
  onFailedJob: actions.removeJob,
  cancelSim: actions.cancelJob,
  onClickPreferEquipped: actions.togglePreferEquipped,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(SimbotReport));
